'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_COLLECTION,
  GET_COLLECTION,
  UPDATE_COLLECTION,
} from '../collectionsQueries'
import { CollectionFormValues } from '../types'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import ProductSelect from '@/components/shared/inputComponents/ProductSelect'

const formSchema = z.object({
  collection_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
        meta_information: z
          .object({
            id: z.number().optional(),
            permalink: z.string().optional(),
            meta_title: z.string().optional(),
            meta_description: z.string().optional(),
          })
          .optional(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  images: z.array(z.string()).optional(),
  selected_products: z.array(z.object({ id: z.number() })),
})

interface CollectionFormProps {
  data?: {
    id: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function CollectionForm({
  data,
  onDirtyChange,
  onRequestClose,
}: CollectionFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)

  const {
    data: { findUniqueCollection: collectionData } = {},
    loading: loadingData,
    error,
  } = useQuery(GET_COLLECTION, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<CollectionFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      collection_profiles: [],
      images: [],
      selected_products: [],
    },
  })

  const [updateCollection, { loading: updateLoading }] = useMutation(
    UPDATE_COLLECTION,
    {
      onError: e => {
        toast.error('Failed to update collection')
      },
      onCompleted: () => {
        toast.success('Collection updated successfully')
        form.reset()
      },
      refetchQueries: ['GET_COLLECTIONS'],
    }
  )

  const [createCollection, { loading: createLoading }] = useMutation(
    CREATE_COLLECTION,
    {
      onError: e => {
        toast.error('Failed to create collection')
      },
      onCompleted: response => {
        toast.success('Collection created successfully')
        const newId = response.createNewCollection.id
        setId(newId)
        form.reset()
      },
      refetchQueries: ['GET_COLLECTIONS'],
    }
  )

  useEffect(() => {
    if (!loadingData) {
      if (collectionData) {
        // Reset with existing data, ensuring all languages have profiles
        const existingProfiles =
          collectionData.collection_profiles?.filter(
            (p: any) => p.language && allLanguages.includes(p.language)
          ) ?? []

        // Create profiles for all languages, using existing data where available
        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: any) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
              meta_information: {
                id: existingProfile.meta_information?.id,
                permalink: existingProfile.meta_information?.permalink ?? '',
                meta_title: existingProfile.meta_information?.meta_title ?? '',
                meta_description:
                  existingProfile.meta_information?.meta_description ?? '',
              },
            }
          } else {
            // Create new profile for missing language
            return {
              name: '',
              description: '',
              language: lang,
              meta_information: {
                permalink: '',
                meta_title: '',
                meta_description: '',
              },
            }
          }
        })

        form.reset({
          collection_profiles: allProfiles,
          images: collectionData.main_image
            ? [collectionData.main_image.src]
            : [],
          selected_products: collectionData.product_containers || [],
        })
      } else {
        // For NEW forms, initialize with profiles for all languages
        form.reset({
          collection_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
            meta_information: {
              permalink: '',
              meta_title: '',
              meta_description: '',
            },
          })),
          images: [],
          selected_products: [],
        })
      }
    }
  }, [collectionData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  function onSubmit(values: CollectionFormValues) {
    if (id) {
      updateCollection({
        variables: {
          where: { id },
          collection_profiles: values.collection_profiles.map(p => ({
            id: p.id,
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          images: values.images,
          selected_products: values.selected_products,
        },
      })
    } else {
      createCollection({
        variables: {
          collection_profiles: values.collection_profiles.map(p => ({
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          images: values.images,
          selected_products: values.selected_products,
        },
      })
    }
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Collection</SheetTitle>
          <LanguageSwitcher />
        </div>
        {id && <SheetDescription>ID: {id}</SheetDescription>}
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Name"
                description="This is your public display name."
                control={form.control}
                name={`collection_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`collection_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Title"
                control={form.control}
                name={`collection_profiles.${languageId}.meta_information.meta_title`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Description"
                control={form.control}
                name={`collection_profiles.${languageId}.meta_information.meta_description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Meta Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Permalink"
                control={form.control}
                name={`collection_profiles.${languageId}.meta_information.permalink`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Image"
                description="Upload collection image"
                control={form.control}
                name="images"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Products"
                description="Select products for this collection"
                control={form.control}
                name="selected_products"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ProductSelect field={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loadingData}>
                {id ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
